import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  UserPlusIcon,
  LinkIcon,
  ClipboardDocumentListIcon,
  Squares2X2Icon,
  ViewColumnsIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import KanbanView from '../components/projects/KanbanView';
import WaterfallView from '../components/projects/WaterfallView';

const STATUS_LABELS = { 1: 'Draft', 2: 'Active', 3: 'On Hold', 4: 'Completed', 5: 'Cancelled' };
const PRIORITY_LABELS = { 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Critical' };
const TYPE_LABELS = { 1: 'Operational', 2: 'Infrastructure', 3: 'Security', 4: 'Compliance', 5: 'Other' };
const ROLE_LABELS = { 1: 'Lead', 2: 'Contributor', 3: 'Stakeholder' };

const STATUS_COLORS = {
  1: 'bg-gray-100 text-gray-700',
  2: 'bg-blue-100 text-blue-700',
  3: 'bg-yellow-100 text-yellow-700',
  4: 'bg-green-100 text-green-700',
  5: 'bg-red-100 text-red-700',
};
const PRIORITY_COLORS = {
  1: 'bg-green-100 text-green-700',
  2: 'bg-blue-100 text-blue-700',
  3: 'bg-orange-100 text-orange-700',
  4: 'bg-red-100 text-red-700',
};
const TICKET_STATUS_LABELS = { 1: 'New', 2: 'In Progress', 3: 'Pending', 4: 'Resolved', 5: 'Closed' };
const TICKET_STATUS_COLORS = {
  1: 'bg-blue-100 text-blue-700',
  2: 'bg-yellow-100 text-yellow-700',
  3: 'bg-orange-100 text-orange-700',
  4: 'bg-green-100 text-green-700',
  5: 'bg-gray-100 text-gray-700',
};

function Modal({ title, onClose, children, maxWidth = 'max-w-lg' }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className={`relative bg-white rounded-lg shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────

function OverviewTab({ project, onUpdate, members, onAddMember, onRemoveMember, users }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberForm, setMemberForm] = useState({ userId: '', role: 2 });
  const [addingMember, setAddingMember] = useState(false);

  const startEdit = () => {
    setForm({
      name: project.name,
      description: project.description || '',
      type: project.type,
      status: project.status,
      priority: project.priority,
      startDate: project.startDate ? project.startDate.split('T')[0] : '',
      dueDate: project.dueDate ? project.dueDate.split('T')[0] : '',
      budgetAllocated: project.budgetAllocated,
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate({
        name: form.name,
        description: form.description,
        type: parseInt(form.type),
        status: parseInt(form.status),
        priority: parseInt(form.priority),
        budgetAllocated: parseFloat(form.budgetAllocated) || 0,
        startDate: form.startDate || null,
        dueDate: form.dueDate || null,
      });
      setEditing(false);
    } catch {
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setAddingMember(true);
    try {
      await onAddMember({ userId: memberForm.userId, role: parseInt(memberForm.role) });
      setShowAddMember(false);
      setMemberForm({ userId: '', role: 2 });
    } catch {
      alert('Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  const availableUsers = users.filter(u => !members.some(m => m.userId === u.id));
  const f = form || {};

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Project Details</h3>
          {!editing ? (
            <button onClick={startEdit}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
              <PencilIcon className="h-4 w-4" /> Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-1 text-sm text-white bg-blue-600 px-3 py-1 rounded-md hover:bg-blue-700 disabled:opacity-50">
                <CheckIcon className="h-4 w-4" /> {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setEditing(false)}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 px-2 py-1">
                <XMarkIcon className="h-4 w-4" /> Cancel
              </button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
              <input value={f.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
              <select value={f.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500">
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select value={f.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500">
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Priority</label>
              <select value={f.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500">
                {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Budget (R)</label>
              <input type="number" min="0" step="0.01" value={f.budgetAllocated}
                onChange={e => setForm(p => ({ ...p, budgetAllocated: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
              <input type="date" value={f.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Due Date</label>
              <input type="date" value={f.dueDate} onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
          </div>
        ) : (
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              ['Type', TYPE_LABELS[project.type]],
              ['Status', <span key="s" className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[project.status]}`}>{STATUS_LABELS[project.status]}</span>],
              ['Priority', <span key="p" className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[project.priority]}`}>{PRIORITY_LABELS[project.priority]}</span>],
              ['Start Date', project.startDate ? new Date(project.startDate).toLocaleDateString() : '—'],
              ['Due Date', project.dueDate ? new Date(project.dueDate).toLocaleDateString() : '—'],
              ['Budget', project.budgetAllocated > 0 ? `R${project.budgetAllocated.toLocaleString()}` : '—'],
            ].map(([label, val]) => (
              <div key={label}>
                <dt className="text-xs font-medium text-gray-500">{label}</dt>
                <dd className="mt-1 text-sm text-gray-900">{val}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {/* Team */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Team</h3>
          <button onClick={() => setShowAddMember(true)}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
            <UserPlusIcon className="h-4 w-4" /> Add Member
          </button>
        </div>
        {members.length === 0 ? (
          <p className="text-sm text-gray-400">No team members yet.</p>
        ) : (
          <div className="space-y-2">
            {members.map(m => (
              <div key={m.userId} className="flex items-center justify-between py-1.5">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold">
                    {(m.userName || m.userId || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm text-gray-900">{m.userName || m.userId}</div>
                    <div className="text-xs text-gray-400">{ROLE_LABELS[m.role] || 'Member'}</div>
                  </div>
                </div>
                <button onClick={() => onRemoveMember(m.userId)}
                  className="text-gray-300 hover:text-red-500 transition-colors">
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddMember && (
        <Modal title="Add Team Member" onClose={() => setShowAddMember(false)}>
          <form onSubmit={handleAddMember} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
              <select required value={memberForm.userId}
                onChange={e => setMemberForm(p => ({ ...p, userId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">Select user…</option>
                {availableUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.displayName || u.name || u.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select value={memberForm.role}
                onChange={e => setMemberForm(p => ({ ...p, role: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
                {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowAddMember(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={addingMember || !memberForm.userId}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                {addingMember ? 'Adding…' : 'Add Member'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ── Edit Task Modal ───────────────────────────────────────────────────────────

function EditTaskModal({ task, projectId, phases, onSaved, onDeleted, onClose }) {
  const [form, setForm] = useState({
    title: task.title,
    status: task.status,
    phaseId: task.phaseId || '',
    tag: task.tag || '',
    blockerReason: task.blockerReason || '',
    dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
    requirement: task.requirement || '',
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  useEffect(() => {
    apiService.getTaskComments(projectId, task.id)
      .then(data => { setComments(data || []); setCommentsLoaded(true); })
      .catch(() => setCommentsLoaded(true));
  }, [projectId, task.id]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await apiService.updateProjectTask(projectId, task.id, {
        title: form.title,
        status: parseInt(form.status),
        phaseId: form.phaseId ? parseInt(form.phaseId) : null,
        tag: form.tag || null,
        blockerReason: form.blockerReason || null,
        dueDate: form.dueDate || null,
        requirement: form.requirement || '',
      });
      onSaved(updated);
    } catch {
      alert('Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this task?')) return;
    setDeleting(true);
    try {
      await apiService.deleteProjectTask(projectId, task.id);
      onDeleted(task.id);
    } catch {
      alert('Failed to delete task');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setAddingComment(true);
    try {
      const comment = await apiService.addTaskComment(projectId, task.id, newComment.trim());
      setComments(prev => [...prev, comment]);
      setNewComment('');
    } catch {
      alert('Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await apiService.deleteTaskComment(projectId, task.id, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch {
      alert('Failed to delete comment');
    }
  };

  return (
    <Modal title="Edit Task" onClose={onClose}>
      <form onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input required value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Requirement</label>
          <textarea value={form.requirement} rows={3}
            onChange={e => setForm(p => ({ ...p, requirement: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Describe what is required for this task…" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select value={form.status} onChange={e => setForm(p => ({ ...p, status: parseInt(e.target.value) }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value={1}>To Do</option>
            <option value={2}>In Progress</option>
            <option value={3}>Done</option>
            <option value={4}>Blocked</option>
          </select>
        </div>
        {form.status === 4 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Blocker Reason</label>
            <textarea value={form.blockerReason} rows={2}
              onChange={e => setForm(p => ({ ...p, blockerReason: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="What is blocking this task?" />
          </div>
        )}
        {phases.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phase</label>
            <select value={form.phaseId} onChange={e => setForm(p => ({ ...p, phaseId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
              <option value="">Unassigned</option>
              {phases.map(ph => <option key={ph.id} value={ph.id}>{ph.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tag</label>
          <input value={form.tag} onChange={e => setForm(p => ({ ...p, tag: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Optional tag…" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
          <input type="date" value={form.dueDate} onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
        </div>
        <div className="flex justify-between pt-2">
          <button type="button" onClick={handleDelete} disabled={deleting}
            className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50">
            <TrashIcon className="h-4 w-4" /> {deleting ? 'Deleting…' : 'Delete'}
          </button>
          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>

      {/* Comments */}
      <div className="mt-6 border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Comments</h4>
        {!commentsLoaded ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : comments.length === 0 ? (
          <p className="text-sm text-gray-400 mb-3">No comments yet.</p>
        ) : (
          <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
            {comments.map(c => (
              <div key={c.id} className="flex gap-2 items-start">
                <div className="flex-1 bg-gray-50 rounded-md px-3 py-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium text-gray-700">{c.createdByName || c.createdBy}</span>
                    <span className="text-xs text-gray-400">{new Date(c.createdAt).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{c.content}</p>
                </div>
                <button onClick={() => handleDeleteComment(c.id)}
                  className="mt-1 text-gray-300 hover:text-red-500 flex-shrink-0">
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={handleAddComment} className="flex gap-2">
          <input value={newComment} onChange={e => setNewComment(e.target.value)}
            placeholder="Add a comment…"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
          <button type="submit" disabled={addingComment || !newComment.trim()}
            className="px-3 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
            {addingComment ? '…' : 'Add'}
          </button>
        </form>
      </div>
    </Modal>
  );
}

// ── Tasks Tab ─────────────────────────────────────────────────────────────────

function TasksTab({ projectId, tasks, setTasks, phases, setPhases }) {
  const storageKey = `project-view-${projectId}`;
  const [view, setView] = useState(() => localStorage.getItem(storageKey) || 'kanban');
  const [showAddTask, setShowAddTask] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', status: 1, phaseId: '', tag: '', dueDate: '' });
  const [saving, setSaving] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const switchView = (v) => {
    setView(v);
    localStorage.setItem(storageKey, v);
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const task = await apiService.createProjectTask(projectId, {
        title: taskForm.title.trim(),
        status: parseInt(taskForm.status),
        phaseId: taskForm.phaseId ? parseInt(taskForm.phaseId) : null,
        tag: taskForm.tag || null,
        dueDate: taskForm.dueDate || null,
      });
      setTasks(prev => [...prev, task]);
      setShowAddTask(false);
      setTaskForm({ title: '', status: 1, phaseId: '', tag: '', dueDate: '' });
    } catch {
      alert('Failed to add task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          <button onClick={() => switchView('kanban')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
              view === 'kanban' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            <Squares2X2Icon className="h-4 w-4" /> Kanban
          </button>
          <button onClick={() => switchView('waterfall')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors border-l border-gray-200 ${
              view === 'waterfall' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            <ViewColumnsIcon className="h-4 w-4" /> Waterfall
          </button>
        </div>
        <button onClick={() => setShowAddTask(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
          <PlusIcon className="h-4 w-4" /> Add Task
        </button>
      </div>

      {view === 'kanban' ? (
        <KanbanView
          projectId={projectId}
          tasks={tasks}
          setTasks={setTasks}
          onEditTask={setEditingTask}
        />
      ) : (
        <WaterfallView
          projectId={projectId}
          tasks={tasks}
          setTasks={setTasks}
          phases={phases}
          setPhases={setPhases}
          onAddTask={() => setShowAddTask(true)}
        />
      )}

      {showAddTask && (
        <Modal title="Add Task" onClose={() => setShowAddTask(false)}>
          <form onSubmit={handleAddTask} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input required value={taskForm.title} onChange={e => setTaskForm(p => ({ ...p, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Task title…" />
            </div>
            {phases.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phase</label>
                <select value={taskForm.phaseId} onChange={e => setTaskForm(p => ({ ...p, phaseId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
                  <option value="">Unassigned</option>
                  {phases.map(ph => <option key={ph.id} value={ph.id}>{ph.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tag</label>
              <input value={taskForm.tag} onChange={e => setTaskForm(p => ({ ...p, tag: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Optional tag…" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input type="date" value={taskForm.dueDate} onChange={e => setTaskForm(p => ({ ...p, dueDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowAddTask(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving || !taskForm.title.trim()}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Adding…' : 'Add Task'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editingTask && (
        <EditTaskModal
          task={editingTask}
          projectId={projectId}
          phases={phases}
          onSaved={updated => {
            setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
            setEditingTask(null);
          }}
          onDeleted={taskId => {
            setTasks(prev => prev.filter(t => t.id !== taskId));
            setEditingTask(null);
          }}
          onClose={() => setEditingTask(null)}
        />
      )}
    </div>
  );
}

// ── Budget Tab ────────────────────────────────────────────────────────────────

function BudgetTab({ projectId, budget, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ description: '', amount: '', date: new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiService.addBudgetEntry(projectId, {
        description: form.description.trim(),
        amount: parseFloat(form.amount),
        date: form.date,
      });
      setShowAdd(false);
      setForm({ description: '', amount: '', date: new Date().toISOString().split('T')[0] });
      await onRefresh();
    } catch {
      alert('Failed to add budget entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Remove this budget entry?')) return;
    try {
      await apiService.deleteBudgetEntry(projectId, entryId);
      await onRefresh();
    } catch {
      alert('Failed to remove entry');
    }
  };

  if (!budget) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
      </div>
    );
  }

  const { allocated = 0, spent = 0, remaining = 0, isOverBudget = false, entries = [] } = budget;
  const pct = allocated > 0 ? Math.min((spent / allocated) * 100, 100) : 0;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Allocated', value: `R${allocated.toLocaleString()}`, color: 'text-gray-900' },
          { label: 'Spent', value: `R${spent.toLocaleString()}`, color: isOverBudget ? 'text-red-600' : 'text-gray-900' },
          {
            label: 'Remaining',
            value: `R${Math.abs(remaining).toLocaleString()}${remaining < 0 ? ' over' : ''}`,
            color: isOverBudget ? 'text-red-600' : 'text-green-600',
          },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
            <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
          </div>
        ))}
      </div>

      {allocated > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1.5">
            <span>{pct.toFixed(0)}% spent</span>
            <span className={isOverBudget ? 'text-red-600 font-medium' : ''}>
              {isOverBudget ? 'Over budget!' : `R${remaining.toLocaleString()} remaining`}
            </span>
          </div>
          <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${isOverBudget ? 'bg-red-500' : 'bg-blue-500'}`}
              style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700">Budget Entries</h3>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
            <PlusIcon className="h-4 w-4" /> Add Entry
          </button>
        </div>
        {entries.length === 0 ? (
          <p className="px-5 py-8 text-sm text-gray-400 text-center">No budget entries yet.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-2.5 text-left text-xs font-medium text-gray-500">Description</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">Amount</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Date</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Logged By</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {entries.map(entry => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 text-gray-900">{entry.description}</td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">R{entry.amount.toLocaleString()}</td>
                  <td className="px-4 py-3 text-gray-500">{new Date(entry.date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-gray-500">{entry.loggedByName || entry.loggedBy}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleDelete(entry.id)}
                      className="text-gray-300 hover:text-red-500 transition-colors">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && (
        <Modal title="Add Budget Entry" onClose={() => setShowAdd(false)}>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <input required value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="e.g. Server hardware purchase" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount (R) *</label>
              <input required type="number" min="0.01" step="0.01" value={form.amount}
                onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input required type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowAdd(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Adding…' : 'Add Entry'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ── Tickets Tab ───────────────────────────────────────────────────────────────

function TicketsTab({ projectId, tickets, onRefresh }) {
  const [showLink, setShowLink] = useState(false);
  const [ticketSearch, setTicketSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(null);

  const handleSearch = async () => {
    if (!ticketSearch.trim()) return;
    setSearching(true);
    try {
      const results = await apiService.getTickets();
      const q = ticketSearch.toLowerCase();
      const filtered = (Array.isArray(results) ? results : [])
        .filter(t =>
          (t.title?.toLowerCase().includes(q) || String(t.id).includes(q)) &&
          !tickets.some(lt => lt.ticketId === t.id)
        )
        .slice(0, 10);
      setSearchResults(filtered);
    } catch {
      alert('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleLink = async (ticketId) => {
    setLinking(ticketId);
    try {
      await apiService.linkTicketToProjectFromProject(projectId, ticketId);
      setShowLink(false);
      setTicketSearch('');
      setSearchResults([]);
      await onRefresh();
    } catch (err) {
      const is409 = err?.message?.includes('409') || err?.status === 409;
      alert(is409 ? 'This ticket is already linked to another project.' : 'Failed to link ticket.');
    } finally {
      setLinking(null);
    }
  };

  const handleUnlink = async (ticketId) => {
    if (!window.confirm('Unlink this ticket from the project?')) return;
    try {
      await apiService.unlinkTicketFromProject(projectId, ticketId);
      await onRefresh();
    } catch {
      alert('Failed to unlink ticket');
    }
  };

  const closeModal = () => {
    setShowLink(false);
    setTicketSearch('');
    setSearchResults([]);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm text-gray-500">{tickets.length} linked ticket{tickets.length !== 1 ? 's' : ''}</span>
        <button onClick={() => setShowLink(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
          <LinkIcon className="h-4 w-4" /> Link Ticket
        </button>
      </div>

      {tickets.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-400">
          No tickets linked to this project yet.
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-2.5 text-left text-xs font-medium text-gray-500">#</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Title</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tickets.map(t => (
                <tr key={t.ticketId} className="hover:bg-gray-50">
                  <td className="px-5 py-3 text-gray-400 font-mono text-xs">#{t.ticketId}</td>
                  <td className="px-4 py-3">
                    <Link to={`/ticket/${t.ticketId}`} className="text-blue-600 hover:underline font-medium">
                      {t.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${TICKET_STATUS_COLORS[t.status] || 'bg-gray-100 text-gray-600'}`}>
                      {TICKET_STATUS_LABELS[t.status] || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleUnlink(t.ticketId)}
                      className="text-gray-300 hover:text-red-500 transition-colors" title="Unlink">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showLink && (
        <Modal title="Link Ticket" onClose={closeModal}>
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                autoFocus
                value={ticketSearch}
                onChange={e => setTicketSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search by ID or title…"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button onClick={handleSearch} disabled={searching || !ticketSearch.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                {searching ? '…' : 'Search'}
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto rounded-md border border-gray-200">
                {searchResults.map(t => (
                  <div key={t.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
                    <div>
                      <span className="text-xs text-gray-400 mr-2">#{t.id}</span>
                      <span className="text-sm text-gray-900">{t.title}</span>
                    </div>
                    <button onClick={() => handleLink(t.id)} disabled={linking === t.id}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50">
                      {linking === t.id ? '…' : 'Link'}
                    </button>
                  </div>
                ))}
              </div>
            )}
            {ticketSearch && !searching && searchResults.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-2">No matching unlinked tickets found.</p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Notes Tab ─────────────────────────────────────────────────────────────────

function NotesTab({ description, onSave }) {
  const [notes, setNotes] = useState(description || '');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(notes);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch {
      alert('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Project Notes</h3>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
          <CheckIcon className="h-4 w-4" />
          {savedFlash ? 'Saved!' : saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        rows={14}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
        placeholder="Add project notes, decisions, meeting summaries, context…"
      />
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const projectId = parseInt(id);

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [phases, setPhases] = useState([]);
  const [budget, setBudget] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [proj, taskList, memberList, phaseList, budgetData, ticketList, userList] = await Promise.all([
        apiService.getProjectById(projectId),
        apiService.getProjectTasks(projectId),
        apiService.getProjectMembers(projectId),
        apiService.getProjectPhases(projectId),
        apiService.getProjectBudget(projectId),
        apiService.getProjectTickets(projectId),
        apiService.getUsers(),
      ]);
      setProject(proj);
      setTasks(Array.isArray(taskList) ? taskList : []);
      setMembers(Array.isArray(memberList) ? memberList : []);
      setPhases(Array.isArray(phaseList) ? phaseList : []);
      setBudget(budgetData);
      setTickets(Array.isArray(ticketList) ? ticketList : []);
      setUsers(Array.isArray(userList) ? userList : []);
    } catch (err) {
      console.error('Failed to load project', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = async (data) => {
    const updated = await apiService.updateProject(projectId, data);
    setProject(updated);
    return updated;
  };

  const handleAddMember = async (data) => {
    await apiService.addProjectMember(projectId, data);
    const updated = await apiService.getProjectMembers(projectId);
    setMembers(Array.isArray(updated) ? updated : []);
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the project?')) return;
    try {
      await apiService.removeProjectMember(projectId, userId);
      setMembers(prev => prev.filter(m => m.userId !== userId));
    } catch {
      alert('Failed to remove member');
    }
  };

  const refreshBudget = async () => {
    const b = await apiService.getProjectBudget(projectId);
    setBudget(b);
  };

  const refreshTickets = async () => {
    const t = await apiService.getProjectTickets(projectId);
    setTickets(Array.isArray(t) ? t : []);
  };

  const handleSaveNotes = async (notes) => {
    await handleUpdate({
      name: project.name,
      description: notes,
      type: project.type,
      status: project.status,
      priority: project.priority,
      budgetAllocated: project.budgetAllocated,
      startDate: project.startDate || null,
      dueDate: project.dueDate || null,
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 text-center text-gray-500">
        Project not found.{' '}
        <button onClick={() => navigate('/projects')} className="text-blue-600 hover:underline">Back to Projects</button>
      </div>
    );
  }

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'tasks', label: `Tasks (${tasks.length})` },
    { id: 'budget', label: 'Budget' },
    { id: 'tickets', label: `Tickets (${tickets.length})` },
    { id: 'notes', label: 'Notes' },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <button onClick={() => navigate('/projects')} className="mt-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <ClipboardDocumentListIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
            <h1 className="text-2xl font-bold text-gray-900 truncate">{project.name}</h1>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${STATUS_COLORS[project.status]}`}>
              {STATUS_LABELS[project.status]}
            </span>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${PRIORITY_COLORS[project.priority]}`}>
              {PRIORITY_LABELS[project.priority]}
            </span>
          </div>
          {project.description && (
            <p className="mt-1 text-sm text-gray-500 line-clamp-1 ml-8">{project.description}</p>
          )}
        </div>
        <button
          onClick={async () => {
            setExporting(true);
            try { await apiService.exportProject(projectId, project.name); }
            catch { alert('Export failed. Please try again.'); }
            finally { setExporting(false); }
          }}
          disabled={exporting}
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          {exporting ? 'Exporting…' : 'Export'}
        </button>
        <button
          onClick={async () => {
            if (!window.confirm(`Delete project "${project.name}"? This will permanently remove all phases, tasks, and budget entries. Linked tickets will be unlinked but not deleted. This cannot be undone.`)) return;
            setDeleting(true);
            try {
              await apiService.deleteProject(projectId);
              navigate('/projects');
            } catch {
              alert('Failed to delete project.');
              setDeleting(false);
            }
          }}
          disabled={deleting}
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-300 rounded-md text-red-600 bg-white hover:bg-red-50 disabled:opacity-50"
        >
          <TrashIcon className="h-4 w-4" />
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <OverviewTab
          project={project}
          onUpdate={handleUpdate}
          members={members}
          onAddMember={handleAddMember}
          onRemoveMember={handleRemoveMember}
          users={users}
        />
      )}
      {activeTab === 'tasks' && (
        <TasksTab
          projectId={projectId}
          tasks={tasks}
          setTasks={setTasks}
          phases={phases}
          setPhases={setPhases}
        />
      )}
      {activeTab === 'budget' && (
        <BudgetTab
          projectId={projectId}
          budget={budget}
          onRefresh={refreshBudget}
        />
      )}
      {activeTab === 'tickets' && (
        <TicketsTab
          projectId={projectId}
          tickets={tickets}
          onRefresh={refreshTickets}
        />
      )}
      {activeTab === 'notes' && (
        <NotesTab
          description={project.description}
          onSave={handleSaveNotes}
        />
      )}
    </div>
  );
}
